import React, {useRef, useState} from 'react';
import {
    Box,
    Button,
    ClickAwayListener,
    Divider,
    FormControl,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Select,
    SelectChangeEvent,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import {FormPresentationType, FormThemeSettings, Palette} from "crqlar-react-common/dist/models/forms/form";
import CColorPicker from "crqlar-react-common/dist/components/CColorPicker";
import ImagePicker from "crqlar-react-common/dist/components/ImagePicker";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {useTranslation} from "react-i18next";

// Theme presets for single-page forms
export const singlePageThemePresets: Record<string, FormThemeSettings> = {
    indigo: {
        preset: 'indigo',
        palette: {
            mode: 'light',
            primary: '#3F51B5',
            secondary: '#a5aeda',
            textPrimary: '#212121',
            textSecondary: '#757575',
        },
        typography: {
            fontFamily: "Roboto",
            heading: {fontSize: '32px', fontWeight: 'bold'},
            question: {fontSize: '16px', fontWeight: 'normal'},
            body: {fontSize: '14px', fontWeight: 'normal'},
            button: {fontSize: '16px', fontWeight: 'bold'},
        },
        shape: {
            borderRadius: 8,
            questionSpacing: 50
        },
    },
    teal: {
        preset: 'teal',
        palette: {
            mode: 'light',
            primary: '#009688',
            secondary: '#61bfb4',
            textPrimary: '#212121',
            textSecondary: '#757575',
        },
        typography: {
            fontFamily: "Public Sans",
            heading: {fontSize: '30px', fontWeight: 'bold'},
            question: {fontSize: '18px', fontWeight: 'normal'},
            body: {fontSize: '15px', fontWeight: 'normal'},
            button: {fontSize: '15px', fontWeight: 'bold'},
        },
        shape: {
            borderRadius: 4,
            questionSpacing: 50
        },
    },
    purple: {
        preset: 'purple',
        palette: {
            mode: 'light',
            primary: '#E1BEE7',
            secondary: '#dad0dc',
            textPrimary: '#212121',
            textSecondary: '#757575',
            questionBackground: '#e1bee71e'
        },
        typography: {
            fontFamily: "Poppins",
            heading: {fontSize: '34px', fontWeight: 'bold'},
            question: {fontSize: '18px', fontWeight: 'normal'},
            body: {fontSize: '16px', fontWeight: 'normal'},
            button: {fontSize: '16px', fontWeight: 'bold'},
        },
        shape: {
            borderRadius: 4,
            questionSpacing: 50
        },
    },
    red: {
        preset: 'red',
        palette: {
            mode: 'light',
            primary: '#D32F2F',
            secondary: '#df9c9c',
            background: '#171717',
            textPrimary: '#e4e4e4',
            textSecondary: '#aaaaaa',
        },
        typography: {
            fontFamily: "Inter",
            heading: {fontSize: '28px', fontWeight: 'bold'},
            question: {fontSize: '18px', fontWeight: 'normal'},
            body: {fontSize: '15px', fontWeight: 'normal'},
            button: {fontSize: '15px', fontWeight: 'bold'},
        },
        shape: {
            borderRadius: 4,
            questionSpacing: 32,
            questionBorderStyle: '2px dashed #D32F2F'
        },
    },
};

// Theme presets for multi-page forms
export const multiPageThemePresets: Record<string, FormThemeSettings> = {
    indigo: {
        preset: 'indigo',
        palette: {
            mode: 'light',
            primary: '#3F51B5',
            background: '#eff2f6f2',
            secondary: '#805AD5',
            contrastText: '#ffffff',
            textPrimary: '#0d1e9eff',
            textSecondary: '#757575',
            textDisabled: '#CBD5E0'
        },
        typography: {
            fontFamily: "Roboto",
            fontSize: 14,
            heading: {fontSize: '32px', fontWeight: 'bold', lineHeight: '1.2'},
            question: {fontSize: '28px', fontWeight: 'normal', lineHeight: '1.3'},
            body: {fontSize: '20px', fontWeight: '300', lineHeight: '1.3'},
            button: {fontSize: '14px', fontWeight: 'normal', lineHeight: '1.3'}
        },
        shape: {
            borderRadius: 8
        },
    },
    teal: {
        preset: 'teal',
        palette: {
            mode: 'light',
            primary: '#009688',
            background: '#e3f0f0f2',
            secondary: '#4DB6AC',
            contrastText: '#ffffff',
            textPrimary: '#004D40',
            textSecondary: '#757575',
            textDisabled: '#B2DFDB'
        },
        typography: {
            fontFamily: "Public Sans",
            fontSize: 14,
            heading: {fontSize: '30px', fontWeight: 'bold', lineHeight: '1.2'},
            question: {fontSize: '18px', fontWeight: 'normal', lineHeight: '1.3'},
            body: {fontSize: '18px', fontWeight: 'light', lineHeight: '1.3'},
            button: {fontSize: '18px', fontWeight: 'bold', lineHeight: '1.3'}
        },
        shape: {
            borderRadius: 12
        },
    },
    purple: {
        preset: 'purple',
        palette: {
            mode: 'light',
            primary: '#9F7AEA',
            background: '#f4e8fcf2',
            secondary: '#B794F4',
            contrastText: '#ffffff',
            textPrimary: '#44337A',
            textSecondary: '#6B46C1',
            textDisabled: '#D6BCFA'
        },
        typography: {
            fontFamily: "Poppins",
            fontSize: 14,
            heading: {fontSize: '34px', fontWeight: 'bold', lineHeight: '1.2'},
            question: {fontSize: '22px', fontWeight: 'normal', lineHeight: '1.3'},
            body: {fontSize: '22px', fontWeight: 'light', lineHeight: '1.3'},
            button: {fontSize: '22px', fontWeight: 'bold', lineHeight: '1.3'}
        },
        shape: {
            borderRadius: 16
        },
    },
    red: {
        preset: 'red',
        palette: {
            mode: 'light',
            primary: '#E53E3E',
            background: '#fed7d7f2',
            secondary: '#FC8181',
            contrastText: '#ffffff',
            textPrimary: '#742A2A',
            textSecondary: '#C53030',
            textDisabled: '#FEB2B2'
        },
        typography: {
            fontFamily: "Inter",
            fontSize: 14,
            heading: {fontSize: '28px', fontWeight: 'bold', lineHeight: '1.2'},
            question: {fontSize: '19px', fontWeight: 'normal', lineHeight: '1.3'},
            body: {fontSize: '19px', fontWeight: 'light', lineHeight: '1.3'},
            button: {fontSize: '19px', fontWeight: 'bold', lineHeight: '1.3'}
        },
        shape: {
            borderRadius: 8
        },
    },
};

interface DesignPanelProps {
    theme: FormThemeSettings;
    onThemeUpdate: (theme: FormThemeSettings) => void;
    presentationType: FormPresentationType
}

// Typography variant tabs
enum TypographyTab {
    QUESTION = 0,
    BODY = 1,
    BUTTON = 2,
    HEADINGS = 3
}

const DesignPanel: React.FC<DesignPanelProps> = ({theme, onThemeUpdate, presentationType}) => {
    const {t} = useTranslation();
    const [currentTypographyTab, setCurrentTypographyTab] = useState<TypographyTab>(TypographyTab.QUESTION);
    const [selectedPreset, setSelectedPreset] = useState<string>(theme.preset || '');
    const [popperOpen, setPopperOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    // Get the correct theme presets based on presentation type
    const themePresets = presentationType === FormPresentationType.MULTI_PAGE
        ? multiPageThemePresets
        : singlePageThemePresets;

    // Check if single-page specific fields are relevant
    const isSinglePage = presentationType === FormPresentationType.SINGLE_PAGE;

    // Handle theme preset selection
    const handleThemePresetChange = (presetKey: string) => {
        const selectedPreset = themePresets[presetKey];

        onThemeUpdate({
            ...selectedPreset,
            backgroundMedia: theme.backgroundMedia, // Preserve image
            preset: presetKey
        });

        setSelectedPreset(presetKey);
        setPopperOpen(false);
    };

    const handlePopperToggle = () => {
        setPopperOpen((prevOpen) => !prevOpen);
    };

    const handlePopperClose = (event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setPopperOpen(false);
    };

    const getPresetLabel = (key: string) => {
        switch (key) {
            case 'indigo':
                return t('Default');
            case 'teal':
                return t('Calm');
            case 'purple':
                return t('Soft');
            case 'red':
                return t('Energy');
            case 'custom':
                return t('Custom');
            default:
                return t('Select Theme');
        }
    };

    // Handle palette color change
    const handlePaletteColorChange = (field: keyof Palette, value: string) => {
        const newTheme: FormThemeSettings = {
            ...theme,
            palette: {
                ...theme.palette,
                [field]: value
            }
        };
        onThemeUpdate(newTheme);
    };

    // Handle typography change
    const handleTypographyChange = (variant: 'heading' | 'question' | 'body' | 'button', property: string, value: string) => {
        const newTheme = {
            ...theme,
            typography: {
                [variant]: {
                    ...theme.typography?.[variant],
                    [property]: value
                }
            }
        };
        onThemeUpdate(newTheme);
    };

    // Handle font family change
    const handleFontFamilyChange = (event: SelectChangeEvent) => {
        const newTheme = {
            ...theme,
            typography: {
                ...theme.typography,
                fontFamily: event.target.value,
            }
        };
        onThemeUpdate(newTheme);
    };

    // Handle shape change
    const handleBorderRadiusChange = (value: number) => {
        const newTheme = {
            ...theme,
            shape: {
                ...theme.shape,
                borderRadius: value,
            }
        };
        onThemeUpdate(newTheme);
    };

    const handleQuestionSpacingChange = (value: number) => {
        const newTheme = {
            ...theme,
            shape: {
                ...theme.shape,
                questionSpacing: value,
            }
        };
        onThemeUpdate(newTheme);
    };

    const handleQuestionBorderStyleChange = (value?: string) => {
        const newTheme = {
            ...theme,
            shape: {
                ...theme.shape,
                questionBorderStyle: value,
            }
        };
        onThemeUpdate(newTheme);
    };

    const handleFormMinHeightChange = (value?: string) => {
        const newTheme = {
            ...theme,
            shape: {
                ...theme.shape,
                formMinHeight: value,
            }
        };
        onThemeUpdate(newTheme);
    };

    // Handle background media change
    const handleBackgroundMediaChange = (media?: Media) => {
        onThemeUpdate({
            ...theme,
            backgroundMedia: media
        });
    };


    // Get current typography tab content
    const getCurrentTypographyEditor = () => {
        switch (currentTypographyTab) {
            case TypographyTab.HEADINGS:
                const heading = theme.typography?.heading || {};
                const headingFontSize = heading.fontSize || '' // ? parseInt(heading.fontSize) : 32;
                return (
                    <Stack spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                            {t('Applies to all H1 heading')}
                        </Typography>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Size')}</Typography>
                            <TextField
                                value={headingFontSize}
                                onChange={(e) => handleTypographyChange('heading', 'fontSize', e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Weight')}</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={String(heading.fontWeight || 'bold')}
                                    onChange={(e) => handleTypographyChange('heading', 'fontWeight', e.target.value)}
                                    sx={{borderRadius: 1}}
                                >
                                    <MenuItem value="normal">{t('Normal')}</MenuItem>
                                    <MenuItem value="bold">{t('Bold')}</MenuItem>
                                    <MenuItem value="300">{t('Light')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                );

            case TypographyTab.QUESTION:
                const question = theme.typography?.question || {};
                const questionFontSize = question.fontSize || '' // ? parseInt(heading.fontSize) : 32;
                return (
                    <Stack spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                            {t('Applies to all questions')}
                        </Typography>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Size')}</Typography>
                            <TextField
                                value={questionFontSize}
                                onChange={(e) => handleTypographyChange('question', 'fontSize', e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Weight')}</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={String(question.fontWeight || 'bold')}
                                    onChange={(e) => handleTypographyChange('question', 'fontWeight', e.target.value)}
                                    sx={{borderRadius: 1}}
                                >
                                    <MenuItem value="normal">{t('Normal')}</MenuItem>
                                    <MenuItem value="bold">{t('Bold')}</MenuItem>
                                    <MenuItem value="300">{t('Light')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                );

            case TypographyTab.BODY:
                const body = theme.typography?.body || {};
                const bodyFontSize = body.fontSize
                return (
                    <Stack spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                            {t('Applies to body text and descriptions')}
                        </Typography>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Size')}</Typography>
                            <TextField
                                value={bodyFontSize || ''}
                                onChange={(e) => handleTypographyChange('body', 'fontSize', e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Weight')}</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={String(body.fontWeight || 'normal')}
                                    onChange={(e) => handleTypographyChange('body', 'fontWeight', e.target.value)}
                                    sx={{borderRadius: 1}}
                                >
                                    <MenuItem value="normal">{t('Normal')}</MenuItem>
                                    <MenuItem value="bold">{t('Bold')}</MenuItem>
                                    <MenuItem value="300">{t('Light')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                );

            case TypographyTab.BUTTON:
                const button = theme.typography?.button || {};
                const buttonFontSize = button.fontSize || ''
                return (
                    <Stack spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                            {t('Applies to all button text')}
                        </Typography>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Size')}</Typography>
                            <TextField
                                value={buttonFontSize}
                                onChange={(e) => handleTypographyChange('button', 'fontSize', e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Font Weight')}</Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={String(button.fontWeight || '500')}
                                    onChange={(e) => handleTypographyChange('button', 'fontWeight', e.target.value)}
                                    sx={{borderRadius: 1}}
                                >
                                    <MenuItem value="normal">{t('Normal')}</MenuItem>
                                    <MenuItem value="bold">{t('Bold')}</MenuItem>
                                    <MenuItem value="300">{t('Light')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{p: 2, height: '100%', flex: 1, overflow: 'auto'}}>
            <Box>
                {/*/!* Theme Preview Card *!/*/}
                {/*<Card sx={{mb: 3, borderRadius: 2, overflow: 'hidden'}}>*/}
                {/*    <Box sx={{display: 'flex', height: 180}}>*/}
                {/*        /!* Image preview *!/*/}
                {/*        {theme.imageMedia && (*/}
                {/*            <Box*/}
                {/*                sx={{*/}
                {/*                    width: '50%',*/}
                {/*                    backgroundImage: `url(${MediaService.getBigThumbnail(theme.imageMedia)})`,*/}
                {/*                    backgroundSize: 'cover',*/}
                {/*                    backgroundPosition: 'center'*/}
                {/*                }}*/}
                {/*            />*/}
                {/*        )}*/}

                {/*        /!* Color preview *!/*/}
                {/*        <Box*/}
                {/*            sx={{*/}
                {/*                width: theme.imageMedia ? '50%' : '100%',*/}
                {/*                backgroundColor: theme.palette?.background || '#1976d2',*/}
                {/*                display: 'flex',*/}
                {/*                flexDirection: 'column',*/}
                {/*                justifyContent: 'center',*/}
                {/*                alignItems: 'center',*/}
                {/*                padding: 2*/}
                {/*            }}*/}
                {/*        >*/}
                {/*            <Typography*/}
                {/*                sx={{*/}
                {/*                    mb: 2,*/}
                {/*                    color: theme.palette?.textPrimary || '#fff',*/}
                {/*                    fontWeight: theme.typography?.heading?.fontWeight || 'bold',*/}
                {/*                    fontSize: theme.typography?.heading?.fontSize || '32px',*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                Aa*/}
                {/*            </Typography>*/}
                {/*            <Button*/}
                {/*                variant="contained"*/}
                {/*                sx={{*/}
                {/*                    backgroundColor: theme.palette?.primary || '#1976d2',*/}
                {/*                    borderRadius: `${theme.shape?.borderRadius || 8}px`,*/}
                {/*                    fontWeight: theme.typography?.button?.fontWeight || '500',*/}
                {/*                    fontSize: theme.typography?.button?.fontSize || '14px',*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                Button*/}
                {/*            </Button>*/}
                {/*        </Box>*/}
                {/*    </Box>*/}
                {/*</Card>*/}

                {/* Theme Preset Selection */}
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {t('Theme Preset')}
                </Typography>
                <Box sx={{mb: 3}}>
                    <Button
                        ref={anchorRef}
                        variant="outlined"
                        onClick={handlePopperToggle}
                        fullWidth
                        sx={{
                            justifyContent: 'space-between',
                            textAlign: 'left',
                            borderRadius: 1,
                            textTransform: 'none'
                        }}
                    >
                        {getPresetLabel(selectedPreset)}
                    </Button>
                    <Popper
                        open={popperOpen}
                        anchorEl={anchorRef.current}
                        placement="bottom-start"
                        transition
                        style={{zIndex: 1300, width: anchorRef.current?.offsetWidth}}
                    >
                        {({TransitionProps}) => (
                            <Grow {...TransitionProps}>
                                <Paper elevation={3}>
                                    <ClickAwayListener onClickAway={handlePopperClose}>
                                        <MenuList>
                                            <MenuItem onClick={() => handleThemePresetChange('indigo')}>
                                                {t('Default')}
                                            </MenuItem>
                                            <MenuItem onClick={() => handleThemePresetChange('teal')}>
                                                {t('Calm')}
                                            </MenuItem>
                                            <MenuItem onClick={() => handleThemePresetChange('purple')}>
                                                {t('Soft')}
                                            </MenuItem>
                                            <MenuItem onClick={() => handleThemePresetChange('red')}>
                                                {t('Energy')}
                                            </MenuItem>
                                            <MenuItem onClick={() => handleThemePresetChange('custom')}>
                                                {t('Custom')}
                                            </MenuItem>
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </Box>

                <Divider sx={{my: 2}}/>

                {/* Colors Section */}
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {t('Colors')}
                </Typography>

                <Stack spacing={2} sx={{mb: 3}}>
                    {/*<Box>*/}
                    {/*    <Typography variant="body2" gutterBottom>Mode</Typography>*/}
                    {/*    <ToggleButtonGroup*/}
                    {/*        value={theme.palette?.mode || 'light'}*/}
                    {/*        exclusive*/}
                    {/*        onChange={handlePaletteModeChange}*/}
                    {/*        size="small"*/}
                    {/*        fullWidth*/}
                    {/*        sx={{borderRadius: 1}}*/}
                    {/*    >*/}
                    {/*        <ToggleButton value="light" sx={{borderRadius: 1}}>*/}
                    {/*            <LightModeIcon sx={{mr: 1, fontSize: 18}}/>*/}
                    {/*            Light*/}
                    {/*        </ToggleButton>*/}
                    {/*        <ToggleButton value="dark" sx={{borderRadius: 1}}>*/}
                    {/*            <DarkModeIcon sx={{mr: 1, fontSize: 18}}/>*/}
                    {/*            Dark*/}
                    {/*        </ToggleButton>*/}
                    {/*    </ToggleButtonGroup>*/}
                    {/*</Box>*/}

                    <Box>
                        <Typography variant="body2" gutterBottom>{t('Primary Color')}</Typography>
                        <CColorPicker
                            value={theme.palette?.primary}
                            onChange={color => handlePaletteColorChange('primary', color)}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" gutterBottom>{t('Secondary Color')}</Typography>
                        <CColorPicker
                            value={theme.palette?.secondary}
                            onChange={color => handlePaletteColorChange('secondary', color)}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" gutterBottom>{t('Form Background Color')}</Typography>
                        <CColorPicker
                            value={theme.palette?.background}
                            onChange={color => handlePaletteColorChange('background', color)}
                        />
                    </Box>

                    {isSinglePage && (
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Question Background Color')}</Typography>
                            <CColorPicker
                                value={theme.palette?.questionBackground}
                                onChange={color => handlePaletteColorChange('questionBackground', color)}
                            />
                        </Box>
                    )}

                    <Box>
                        <Typography variant="body2" gutterBottom>{t('Text Color')}</Typography>
                        <CColorPicker
                            value={theme.palette?.textPrimary}
                            onChange={color => handlePaletteColorChange('textPrimary', color)}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" gutterBottom>{t('Secondary Text Color')}</Typography>
                        <CColorPicker
                            value={theme.palette?.textSecondary}
                            onChange={color => handlePaletteColorChange('textSecondary', color)}
                        />
                    </Box>

                </Stack>

                {
                    presentationType === FormPresentationType.MULTI_PAGE &&
                    <>
                        <Divider sx={{my: 2}}/>

                        {/* Background Media Section */}
                        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            {t('Background Media')}
                        </Typography>
                        <Stack spacing={1} sx={{mb: 3}}>
                            <Typography variant="body2">{t('Background Image')}</Typography>
                            <ImagePicker
                                image={theme.backgroundMedia}
                                setImage={handleBackgroundMediaChange}
                                height={200}
                                width={'100%'}
                            />
                        </Stack>
                    </>
                }

                <Divider sx={{my: 2}}/>

                {/* Typography Section */}
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {t('Typography')}
                </Typography>

                <Box sx={{mb: 2}}>
                    <Typography variant="body2" gutterBottom>{t('Font Family')}</Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={theme.typography?.fontFamily || ''}
                            onChange={handleFontFamilyChange}
                            sx={{borderRadius: 1}}
                        >
                            <MenuItem value="Inter">Inter</MenuItem>
                            <MenuItem value="Roboto">Roboto</MenuItem>
                            <MenuItem value="Public Sans">Public Sans</MenuItem>
                            <MenuItem value="Poppins">Poppins</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Tabs
                    value={currentTypographyTab}
                    onChange={(_, newValue) => setCurrentTypographyTab(newValue)}
                    sx={{mb: 2, borderBottom: 1, borderColor: 'divider'}}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label={t('Question')}/>
                    <Tab label={t('Body')}/>
                    <Tab label={t('Button')}/>
                    <Tab label={t('Heading / Title')}/>
                </Tabs>

                {getCurrentTypographyEditor()}

                <Divider sx={{my: 2}}/>

                {/* Shape Section */}
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    {t('Shape')}
                </Typography>

                <Box sx={{mb: 2}}>
                    <Typography variant="body2" gutterBottom>{t('Border Radius')}</Typography>
                    <TextField
                        type="number"
                        value={theme.shape?.borderRadius || ''}
                        onChange={(e) => handleBorderRadiusChange(Number(e.target.value))}
                        size="small"
                        fullWidth
                        InputProps={{sx: {borderRadius: 1}}}
                    />
                </Box>

                {isSinglePage && (
                    <>
                        <Box sx={{mb: 2}}>
                            <Typography variant="body2" gutterBottom>{t('Question Spacing')}</Typography>
                            <TextField
                                type="number"
                                value={theme.shape?.questionSpacing || ''}
                                onChange={(e) => handleQuestionSpacingChange(Number(e.target.value))}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>

                        <Box sx={{mb: 2}}>
                            <Typography variant="body2" gutterBottom>{t('Question Border Style')}</Typography>
                            <TextField
                                value={theme.shape?.questionBorderStyle || ''}
                                onChange={(e) => handleQuestionBorderStyleChange(e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>
                    </>
                )}

                {!isSinglePage && (
                    <>
                        <Box sx={{mb: 2}}>
                            <Typography variant="body2" gutterBottom>
                                {t('Min form height (eg. 300px, 50vh, 40%, etc..)')}
                            </Typography>
                            <TextField
                                value={theme.shape?.formMinHeight || ''}
                                onChange={(e) => handleFormMinHeightChange(e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{sx: {borderRadius: 1}}}
                            />
                        </Box>
                    </>
                )}

            </Box>
        </Box>
    );
};

export default DesignPanel;
